import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { CheckCircle, ArrowRight, ArrowLeft, Sparkles, Mail } from 'lucide-react';
import { PART1_QUESTIONS, SYMPTOMS, PILLAR_DETAILS, CATEGORY_TO_SUBCATEGORY } from './wellnessAssessmentData';

// Set OG meta tags for social sharing
const useMetaTags = () => {
  useEffect(() => {
    const ogTags = {
      'og:title': 'Wellness Assessment | Luminelle',
      'og:description': 'Discover your primary wellness focus area in 2 minutes. Personalized results for women navigating perimenopause and menopause.',
      'og:image': `${window.location.origin}/assets/wellness-assessment-og.jpg`,
      'og:url': `${window.location.origin}/wellness-assessment`,
      'og:type': 'website',
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Wellness Assessment | Luminelle',
      'twitter:description': 'Discover your primary wellness focus area in 2 minutes. Personalized results for women navigating perimenopause and menopause.',
      'twitter:image': `${window.location.origin}/assets/wellness-assessment-og.jpg`,
    };
    const originals = {};
    Object.entries(ogTags).forEach(([key, value]) => {
      const attr = 'property';
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (el) {
        originals[key] = { attr, content: el.getAttribute('content') };
        el.setAttribute('content', value);
      } else {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        el.setAttribute('content', value);
        document.head.appendChild(el);
        originals[key] = { attr, content: null };
      }
    });
    const origTitle = document.title;
    document.title = 'Wellness Assessment | Luminelle';
    return () => {
      document.title = origTitle;
      Object.entries(originals).forEach(([key, { attr, content }]) => {
        const el = document.querySelector(`meta[${attr}="${key}"]`);
        if (!el) return;
        if (content === null) el.remove();
        else el.setAttribute('content', content);
      });
    };
  }, []);
};

const API = process.env.REACT_APP_BACKEND_URL;

const WellnessAssessmentPage = ({ onOpenSignup }) => {
  useMetaTags();
  const [step, setStep] = useState('intro');
  const [part1Answers, setPart1Answers] = useState({});
  const [primaryPillar, setPrimaryPillar] = useState(null);
  const [part2Symptoms, setPart2Symptoms] = useState({});
  const [results, setResults] = useState(null);
  const [expandedPillar, setExpandedPillar] = useState(null);

  // Email capture state
  const [leadEmail, setLeadEmail] = useState('');
  const [leadName, setLeadName] = useState('');
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  const [leadMessage, setLeadMessage] = useState('');

  const handlePart1Answer = (questionId, answer) => {
    setPart1Answers(prev => ({ ...prev, [questionId]: answer }));
  };

  const calculatePrimaryPillar = () => {
    const counts = { A: 0, B: 0, C: 0 };
    Object.values(part1Answers).forEach(answer => { counts[answer]++; });
    if (counts.A >= counts.B && counts.A >= counts.C) return 'BODY';
    if (counts.B > counts.A && counts.B >= counts.C) return 'MIND';
    return 'VITALITY';
  };

  const calculatePillarScores = () => {
    const counts = { A: 0, B: 0, C: 0 };
    Object.values(part1Answers).forEach(answer => { counts[answer]++; });
    const total = 5;
    return {
      BODY: Math.round((counts.A / total) * 100),
      MIND: Math.round((counts.B / total) * 100),
      VITALITY: Math.round((counts.C / total) * 100)
    };
  };

  const handlePart1Complete = () => {
    if (Object.keys(part1Answers).length < 5) return;
    const pillar = calculatePrimaryPillar();
    setPrimaryPillar(pillar);
    setExpandedPillar(pillar);
    setStep('part2');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSymptomToggle = (category, symptom) => {
    setPart2Symptoms(prev => {
      const categorySymptoms = prev[category] || [];
      const isSelected = categorySymptoms.includes(symptom);
      return {
        ...prev,
        [category]: isSelected
          ? categorySymptoms.filter(s => s !== symptom)
          : [...categorySymptoms, symptom]
      };
    });
  };

  const handleSubmit = () => {
    const categoryCounts = {};
    Object.entries(part2Symptoms).forEach(([category, symptoms]) => {
      if (symptoms.length > 0) categoryCounts[category] = symptoms.length;
    });
    const sortedCategories = Object.entries(categoryCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([category]) => category);

    const totalSymptoms = Object.values(part2Symptoms).reduce((sum, s) => sum + s.length, 0);
    const pillarScores = calculatePillarScores();

    // Calculate secondary pillar from symptoms
    const pillarSymptomCounts = { BODY: 0, MIND: 0, VITALITY: 0 };
    Object.entries(part2Symptoms).forEach(([category, symptoms]) => {
      const mapping = CATEGORY_TO_SUBCATEGORY[category];
      if (mapping) {
        if (mapping.pillar === 'radiant_body') pillarSymptomCounts.BODY += symptoms.length;
        else if (mapping.pillar === 'balanced_mind') pillarSymptomCounts.MIND += symptoms.length;
        else if (mapping.pillar === 'enduring_vitality') pillarSymptomCounts.VITALITY += symptoms.length;
      }
    });

    const sortedPillars = Object.entries(pillarSymptomCounts)
      .filter(([_, count]) => count > 0)
      .sort((a, b) => b[1] - a[1]);

    const secondaryPillar = sortedPillars.length > 1 ? sortedPillars[1][0] : null;

    setResults({
      primary_pillar: primaryPillar,
      secondary_pillar: secondaryPillar,
      pillar_scores: pillarScores,
      pillar_symptom_counts: pillarSymptomCounts,
      top_categories: sortedCategories.slice(0, 4),
      total_symptoms: totalSymptoms
    });
    setStep('results');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRetake = () => {
    setPart1Answers({});
    setPart2Symptoms({});
    setPrimaryPillar(null);
    setResults(null);
    setExpandedPillar(null);
    setStep('intro');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isPart1Complete = Object.keys(part1Answers).length === 5;
  const hasPart2Symptoms = Object.values(part2Symptoms).some(s => s.length > 0);
  const progressSteps = ['intro', 'part1', 'part2', 'results'];
  const progressIndex = progressSteps.indexOf(step);

  // Pillar order: primary first, then others
  const pillarOrder = primaryPillar
    ? [primaryPillar, ...['BODY', 'MIND', 'VITALITY'].filter(p => p !== primaryPillar)]
    : ['BODY', 'MIND', 'VITALITY'];

  return (
    <div className="min-h-screen bg-cream" data-testid="wellness-assessment-page">
      {/* Header */}
      <header className="bg-burgundy text-cream py-4 px-4 md:px-8">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <a href="/" className="flex items-center" data-testid="assessment-logo-link">
            <img src="/assets/luminelle-logo.png" alt="Luminelle" className="h-10 w-auto" />
          </a>
          <button onClick={onOpenSignup} className="px-4 py-2 bg-gold text-burgundy font-semibold rounded-full hover:bg-gold/90 transition-colors text-sm" data-testid="assessment-header-join-btn">
            Join Luminelle
          </button>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-burgundy/10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-2">
            {['Welcome', 'Focus Area', 'Symptoms', 'Results'].map((label, i) => (
              <div key={label} className="flex items-center flex-1">
                <div className="flex items-center gap-2 flex-1">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all duration-300 ${
                    i < progressIndex ? 'bg-dark-teal text-cream' : i === progressIndex ? 'bg-burgundy text-cream ring-2 ring-burgundy/30 ring-offset-2' : 'bg-burgundy/10 text-burgundy/40'
                  }`}>
                    {i < progressIndex ? <CheckCircle className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-xs font-medium hidden sm:block ${i <= progressIndex ? 'text-burgundy' : 'text-burgundy/40'}`}>{label}</span>
                </div>
                {i < 3 && <div className={`h-0.5 flex-1 mx-2 rounded transition-all duration-300 ${i < progressIndex ? 'bg-dark-teal' : 'bg-burgundy/10'}`} />}
              </div>
            ))}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* INTRO */}
        {step === 'intro' && (
          <div className="text-center max-w-2xl mx-auto" data-testid="assessment-intro">
            <div className="mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-burgundy/10 text-burgundy rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                2-Minute Wellness Check
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-burgundy leading-tight mb-4">
                Where Does Your Wellness Need Attention?
              </h1>
              <p className="text-base lg:text-lg text-burgundy/70 leading-relaxed max-w-xl mx-auto">
                This assessment identifies your primary wellness focus and the specific symptoms to address first — then connects you to the content that matters most.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { pillar: 'Radiant Body', desc: 'Temperature changes, weight, skin, movement, and physical comfort', icon: '1' },
                { pillar: 'Balanced Mind', desc: 'Mental clarity, mood, anxiety, and emotional balance', icon: '2' },
                { pillar: 'Enduring Vitality', desc: 'Sleep quality, energy levels, and libido restoration', icon: '3' }
              ].map((item) => (
                <div key={item.pillar} className="bg-white rounded-xl p-5 border border-burgundy/10 text-left">
                  <div className="w-9 h-9 rounded-full bg-burgundy/10 flex items-center justify-center text-burgundy font-bold text-sm mb-3">{item.icon}</div>
                  <h3 className="font-serif font-bold text-burgundy text-lg mb-1">{item.pillar}</h3>
                  <p className="text-sm text-burgundy/60">{item.desc}</p>
                </div>
              ))}
            </div>
            <Button onClick={() => { setStep('part1'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 transition-opacity px-10 py-6 text-lg rounded-full shadow-lg" data-testid="assessment-start-btn">
              Start Assessment <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="mt-4 text-sm text-burgundy/50">No account required. Your results are shown instantly.</p>
          </div>
        )}

        {/* PART 1 */}
        {step === 'part1' && (
          <div data-testid="assessment-part1">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-burgundy mb-2">Part 1: Identify Your Focus</h2>
              <p className="text-burgundy/60">5 questions to discover which area needs the most attention right now.</p>
            </div>
            <div className="space-y-5">
              {PART1_QUESTIONS.map((q, index) => (
                <div key={q.id} className="bg-white rounded-xl p-5 md:p-6 border border-burgundy/10 shadow-sm" data-testid={`assessment-q${index + 1}`}>
                  <h3 className="text-base md:text-lg font-semibold text-burgundy mb-4">
                    <span className="text-dark-teal mr-2">{index + 1}.</span>{q.question}
                  </h3>
                  <div className="space-y-2.5">
                    {Object.entries(q.options).map(([letter, text]) => (
                      <label key={letter} className={`flex items-start gap-3 p-3.5 rounded-lg border-2 cursor-pointer transition-all ${
                        part1Answers[q.id] === letter ? 'border-burgundy bg-burgundy/5 shadow-sm' : 'border-burgundy/15 hover:border-burgundy/30'
                      }`} data-testid={`assessment-q${index + 1}-option-${letter}`}>
                        <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                          part1Answers[q.id] === letter ? 'border-burgundy bg-burgundy' : 'border-burgundy/30'
                        }`}>
                          {part1Answers[q.id] === letter && <div className="w-2 h-2 rounded-full bg-cream" />}
                        </div>
                        <input type="radio" name={q.id} value={letter} checked={part1Answers[q.id] === letter} onChange={() => handlePart1Answer(q.id, letter)} className="sr-only" />
                        <span className="text-burgundy/80 text-sm md:text-base">{text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-8">
              <Button onClick={() => setStep('intro')} variant="outline" className="border-burgundy/20 text-burgundy hover:bg-burgundy/5" data-testid="assessment-back-to-intro">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={handlePart1Complete} disabled={!isPart1Complete} className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 transition-opacity px-8 py-5 rounded-full disabled:opacity-40" data-testid="assessment-part1-continue">
                Continue to Symptoms <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* PART 2: ALL Pillars */}
        {step === 'part2' && (
          <div data-testid="assessment-part2">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-burgundy mb-2">Part 2: Select Your Symptoms</h2>
              <p className="text-burgundy/60 max-w-lg mx-auto">
                Check everything that applies across all three areas. Your primary focus is highlighted, but many women experience symptoms in multiple pillars.
              </p>
            </div>

            {pillarOrder.map((pillarKey) => {
              const pillar = SYMPTOMS[pillarKey];
              const isPrimary = pillarKey === primaryPillar;
              const isExpanded = expandedPillar === pillarKey;
              const pillarSymptomCount = Object.keys(pillar.categories).reduce((sum, cat) => sum + (part2Symptoms[cat]?.length || 0), 0);

              return (
                <div key={pillarKey} className={`mb-4 rounded-xl border-2 overflow-hidden transition-all ${isPrimary ? 'border-burgundy/30' : 'border-burgundy/10'}`}>
                  <button
                    onClick={() => setExpandedPillar(isExpanded ? null : pillarKey)}
                    className={`w-full flex items-center justify-between p-4 transition-colors ${isPrimary ? 'bg-burgundy/5' : 'bg-white hover:bg-cream/50'}`}
                    data-testid={`assessment-pillar-toggle-${pillarKey.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        pillarKey === 'BODY' ? 'bg-burgundy text-cream' : pillarKey === 'MIND' ? 'bg-dark-teal text-cream' : 'bg-gold text-burgundy'
                      }`}>
                        {pillar.label}
                      </div>
                      {isPrimary && <span className="text-xs font-medium text-burgundy bg-gold/30 px-2 py-0.5 rounded-full">Your Primary Focus</span>}
                      {pillarSymptomCount > 0 && <span className="text-xs text-burgundy/60">{pillarSymptomCount} selected</span>}
                    </div>
                    <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      <svg className="w-5 h-5 text-burgundy/40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="p-4 pt-0 space-y-4">
                      {Object.entries(pillar.categories).map(([category, symptoms]) => (
                        <div key={category} className="bg-white rounded-lg p-4 border border-burgundy/10 mt-3" data-testid={`assessment-category-${category.replace(/[^a-zA-Z]/g, '-').toLowerCase()}`}>
                          <h3 className="font-serif font-bold text-burgundy text-sm mb-1">{category}</h3>
                          <p className="text-[11px] text-burgundy/50 mb-3">Select all that apply</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {symptoms.map((symptom, si) => {
                              const isChecked = part2Symptoms[category]?.includes(symptom) || false;
                              return (
                                <label key={si} className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
                                  isChecked ? 'border-dark-teal bg-dark-teal/5 shadow-sm' : 'border-burgundy/10 hover:border-burgundy/20'
                                }`}>
                                  <div className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center shrink-0 border-2 transition-all ${
                                    isChecked ? 'border-dark-teal bg-dark-teal' : 'border-burgundy/25'
                                  }`}>
                                    {isChecked && <CheckCircle className="w-3 h-3 text-cream" />}
                                  </div>
                                  <input type="checkbox" checked={isChecked} onChange={() => handleSymptomToggle(category, symptom)} className="sr-only" />
                                  <span className="text-xs text-burgundy/75">{symptom}</span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex justify-between items-center pt-6">
              <Button onClick={() => setStep('part1')} variant="outline" className="border-burgundy/20 text-burgundy hover:bg-burgundy/5" data-testid="assessment-back-to-part1">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button onClick={handleSubmit} disabled={!hasPart2Symptoms} className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 transition-opacity px-8 py-5 rounded-full disabled:opacity-40" data-testid="assessment-see-results">
                See My Results <CheckCircle className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* RESULTS */}
        {step === 'results' && results && (
          <div data-testid="assessment-results">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-burgundy mb-2">Your Wellness Profile</h2>
              <p className="text-burgundy/60">Based on your responses, here's your personalized focus plan.</p>
            </div>

            {/* Pillar Breakdown */}
            <div className={`${PILLAR_DETAILS[results.primary_pillar].color} rounded-2xl p-6 md:p-8 text-cream mb-6 shadow-lg`} data-testid="assessment-result-pillar">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-cream/70 text-sm font-medium uppercase tracking-wider mb-1">Primary Focus</p>
                  <h3 className="text-3xl md:text-4xl font-serif font-bold text-white" style={{ textShadow: '0 0 20px rgba(212,175,55,0.6), 0 2px 4px rgba(0,0,0,0.3)' }}>{PILLAR_DETAILS[results.primary_pillar].fullLabel}</h3>
                </div>
                <Sparkles className="w-6 h-6 text-cream/50" />
              </div>
              <p className="text-cream/90 leading-relaxed text-sm md:text-base mb-4">{PILLAR_DETAILS[results.primary_pillar].description}</p>

              {/* Pillar Score Bars */}
              {results.pillar_symptom_counts && (
                <div className="space-y-2 mt-4 pt-4 border-t border-cream/20">
                  {Object.entries(results.pillar_symptom_counts).filter(([_, count]) => count > 0).sort((a, b) => b[1] - a[1]).map(([pillar, count]) => (
                    <div key={pillar} className="flex items-center gap-3">
                      <span className="text-cream/80 text-xs w-16">{PILLAR_DETAILS[pillar].label}</span>
                      <div className="flex-1 h-2 bg-cream/20 rounded-full overflow-hidden">
                        <div className="h-full bg-cream/70 rounded-full" style={{ width: `${Math.min(100, (count / results.total_symptoms) * 100)}%` }} />
                      </div>
                      <span className="text-cream/60 text-xs">{count} symptom{count !== 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Priority Symptom Areas */}
            {results.top_categories.length > 0 && (
              <div className="bg-white rounded-xl p-5 md:p-6 border border-burgundy/10 shadow-sm mb-6" data-testid="assessment-result-areas">
                <h3 className="font-serif font-bold text-burgundy text-lg mb-3">Your Priority Areas</h3>
                <p className="text-sm text-burgundy/60 mb-3">Start with these topics in the Classroom for the most relevant content:</p>
                <div className="flex flex-wrap gap-2">
                  {results.top_categories.map((cat, i) => {
                    const mapping = CATEGORY_TO_SUBCATEGORY[cat];
                    const pillarKey = mapping ? (mapping.pillar === 'radiant_body' ? 'BODY' : mapping.pillar === 'balanced_mind' ? 'MIND' : 'VITALITY') : results.primary_pillar;
                    const tagColor = PILLAR_DETAILS[pillarKey].tagColor;
                    return (
                    <button
                      key={cat}
                      onClick={onOpenSignup}
                      className={`px-4 py-2 ${tagColor} text-white rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5`}
                      data-testid={`result-tag-${cat.toLowerCase().replace(/[\s/]+/g, '-')}`}
                    >
                      {cat} <span className="text-white/70">&rarr;</span>
                    </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="bg-white rounded-xl p-5 md:p-6 border border-burgundy/10 shadow-sm mb-6" data-testid="assessment-result-recommendations">
              <h3 className="font-serif font-bold text-burgundy text-lg mb-4">Recommended Next Steps</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PILLAR_DETAILS[results.primary_pillar].recommendations.map((rec, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-cream/50 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-dark-teal flex items-center justify-center text-cream text-xs font-bold shrink-0 mt-0.5">{i + 1}</div>
                    <span className="text-sm text-burgundy/80">{rec}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Newsletter Email Capture */}
            {!leadSubmitted ? (
              <div className="bg-white rounded-xl p-5 md:p-6 border border-burgundy/10 shadow-sm mb-6" data-testid="quiz-email-capture">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-dark-teal/10 flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5 text-dark-teal" />
                  </div>
                  <div>
                    <h3 className="font-serif font-bold text-burgundy text-base">Get Curated Wellness Tips</h3>
                    <p className="text-burgundy/60 text-xs">Subscribe to our bi-weekly newsletter.</p>
                  </div>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!leadEmail) return;
                  setLeadSubmitting(true);
                  try {
                    const resp = await axios.post(`${API}/api/quiz-lead`, {
                      email: leadEmail,
                      name: leadName,
                      primary_pillar: results.primary_pillar,
                      top_areas: results.top_categories
                    });
                    setLeadSubmitted(true);
                    setLeadMessage(resp.data.message);
                  } catch (err) {
                    setLeadMessage(err.response?.data?.detail || 'Something went wrong. Please try again.');
                  } finally {
                    setLeadSubmitting(false);
                  }
                }} className="space-y-2.5">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="text"
                      placeholder="First name (optional)"
                      value={leadName}
                      onChange={(e) => setLeadName(e.target.value)}
                      className="flex-1 px-4 py-2.5 border border-burgundy/20 rounded-lg text-sm focus:border-dark-teal focus:ring-1 focus:ring-dark-teal"
                      data-testid="quiz-lead-name"
                    />
                    <input
                      type="email"
                      placeholder="Your email address"
                      value={leadEmail}
                      onChange={(e) => setLeadEmail(e.target.value)}
                      required
                      className="flex-[2] px-4 py-2.5 border border-burgundy/20 rounded-lg text-sm focus:border-dark-teal focus:ring-1 focus:ring-dark-teal"
                      data-testid="quiz-lead-email"
                    />
                    <Button
                      type="submit"
                      disabled={leadSubmitting || !leadEmail}
                      className="bg-burgundy hover:bg-burgundy/90 text-cream px-6 py-2.5 rounded-lg text-sm whitespace-nowrap disabled:opacity-50"
                      data-testid="quiz-lead-submit"
                    >
                      {leadSubmitting ? 'Subscribing...' : 'Subscribe'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-burgundy/40">We respect your privacy. Unsubscribe anytime.</p>
                </form>
              </div>
            ) : (
              <div className="bg-dark-teal/5 rounded-xl p-5 md:p-6 border border-dark-teal/20 mb-6 flex items-center gap-3" data-testid="quiz-email-success">
                <CheckCircle className="h-6 w-6 text-dark-teal shrink-0" />
                <div>
                  <p className="text-dark-teal font-medium text-sm">{leadMessage}</p>
                  <p className="text-dark-teal/60 text-xs mt-0.5">Look out for our weekly wellness newsletter.</p>
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="bg-gradient-to-r from-burgundy/5 to-dark-teal/5 rounded-2xl p-6 md:p-8 border border-burgundy/10 text-center" data-testid="assessment-result-cta">
              <h3 className="text-xl md:text-2xl font-serif font-bold text-burgundy mb-3">Explore Your Results with a Free Trial</h3>
              <p className="text-burgundy/70 mb-6 max-w-lg mx-auto text-sm md:text-base">
                Your personalized content is ready. Start your free 7-day trial to access expert-led lessons matched to your symptoms, daily wellness trackers, and a supportive community.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={onOpenSignup} className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 transition-opacity px-8 py-5 rounded-full text-base shadow-lg" data-testid="assessment-result-join-btn">
                  Start Your Free Trial <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button onClick={handleRetake} variant="outline" className="border-burgundy/20 text-burgundy hover:bg-burgundy/5 rounded-full px-6 py-5" data-testid="assessment-retake-btn">
                  Retake Assessment
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="bg-burgundy text-cream py-8 px-4 mt-12">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <p className="text-lg font-semibold">Copyright &copy; 2026 Luminelle, a What's Next LLC Company</p>
          <p className="text-xs text-cream/80 max-w-4xl mx-auto">
            Luminelle is an educational community and does not provide medical advice, diagnosis, or treatment.
            Information shared here is for informational purposes only and should not replace guidance from a licensed healthcare provider.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WellnessAssessmentPage;
